import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { categoryAPI, productAPI } from '../api';
import ProductCard from '../components/product/ProductCard';
import { ArrowRight, Truck, Shield, RefreshCw, Headphones, Star } from 'lucide-react';

const CATEGORY_EMOJI = {
  'women-ethnic': '👗', 'women-western': '👚', 'men': '👔', 'kids': '🧒',
  'home-kitchen': '🏠', 'jewellery': '💎', 'beauty-health': '💄', 'electronics': '📱',
};
const CATEGORY_COLORS = ['#fce4f1', '#e8e0ff', '#e0f0ff', '#fff3e0', '#e8f5e9', '#fef3c7', '#e0f7fa'];

const banners = [
  { bg: 'linear-gradient(135deg, #f43397, #6c3fc5)', title: 'Fashion Forward', sub: 'Up to 70% off on all women wear', cta: 'Shop Now', href: '/products?category=women-ethnic' },
  { bg: 'linear-gradient(135deg, #ff6b35, #f43397)', title: 'Fresh Arrivals', sub: 'New collection just dropped', cta: 'Explore', href: '/products' },
  { bg: 'linear-gradient(135deg, #6c3fc5, #3b82f6)', title: 'Electronics Sale', sub: 'Grab the best tech deals', cta: 'Shop Tech', href: '/products?category=electronics' },
];

const features = [
  { icon: <Truck size={24} />, title: 'Free Delivery', sub: 'On orders above ₹499' },
  { icon: <Shield size={24} />, title: 'Secure Payment', sub: '100% safe transactions' },
  { icon: <RefreshCw size={24} />, title: 'Easy Returns', sub: '7 day return policy' },
  { icon: <Headphones size={24} />, title: '24/7 Support', sub: 'Dedicated customer care' },
];

const HomePage = () => {
  const navigate = useNavigate();
  const [activeBanner, setActiveBanner] = React.useState(0);

  React.useEffect(() => {
    const t = setInterval(() => setActiveBanner(b => (b + 1) % banners.length), 4000);
    return () => clearInterval(t);
  }, []);

  const { data: featuredData, isLoading } = useQuery({
    queryKey: ['featured-products'],
    queryFn: () => productAPI.getAll({ featured: 'true', limit: 12 }).then(r => r.data),
  });

  const { data: newData } = useQuery({
    queryKey: ['new-products'],
    queryFn: () => productAPI.getAll({ sort: 'newest', limit: 8 }).then(r => r.data),
  });

  const { data: categoryData } = useQuery({
    queryKey: ['categories'],
    queryFn: () => categoryAPI.getAll().then(r => r.data),
    staleTime: 10 * 60 * 1000,
  });
  const categories = categoryData?.categories || [];

  const banner = banners[activeBanner];

  return (
    <div>
      {/* Hero Banner */}
      <div style={{ background: banner.bg, transition: 'background 0.5s', padding: '60px 0' }}>
        <div className="container" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 40, alignItems: 'center' }}>
          <div>
            <h1 style={{ fontSize: 48, fontWeight: 800, color: 'white', marginBottom: 16, lineHeight: 1.15 }}>
              {banner.title} <br />
              <span style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}>Collection</span>
            </h1>
            <p style={{ color: 'rgba(255,255,255,0.9)', fontSize: 18, marginBottom: 28 }}>{banner.sub}</p>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <Link to={banner.href} style={{ padding: '14px 28px', background: 'white', color: '#f43397', borderRadius: 10, fontWeight: 700, fontSize: 15, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                {banner.cta} <ArrowRight size={18} />
              </Link>
              <Link to="/products" style={{ padding: '14px 28px', background: 'rgba(255,255,255,0.2)', color: 'white', borderRadius: 10, fontWeight: 600, fontSize: 15, border: '2px solid rgba(255,255,255,0.5)' }}>
                View All
              </Link>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
            {banners.map((_, i) => (
              <button key={i} onClick={() => setActiveBanner(i)}
                style={{ width: i === activeBanner ? 32 : 10, height: 10, borderRadius: 10, background: i === activeBanner ? 'white' : 'rgba(255,255,255,0.4)', border: 'none', cursor: 'pointer', transition: 'all 0.3s' }}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Features */}
      <div style={{ background: 'white', borderBottom: '1px solid #f0f0f0' }}>
        <div className="container">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0 }}>
            {features.map((f, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '20px 24px', borderRight: i < 3 ? '1px solid #f0f0f0' : 'none' }}>
                <div style={{ color: '#f43397' }}>{f.icon}</div>
                <div>
                  <p style={{ fontWeight: 600, fontSize: 14 }}>{f.title}</p>
                  <p style={{ fontSize: 12, color: '#9ca3af' }}>{f.sub}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Categories */}
      <div style={{ padding: '3rem 0', background: 'white' }}>
        <div className="container">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
            <h2 style={{ fontSize: 24, fontWeight: 700 }}>Shop by Category</h2>
            <Link to="/products" style={{ color: '#f43397', fontSize: 14, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4 }}>
              See all <ArrowRight size={16} />
            </Link>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: 16 }}>
            {categories.map((cat, i) => (
              <Link key={cat.id} to={`/products?category=${cat.slug}`}
                style={{ textDecoration: 'none', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}
              >
                <div style={{ width: '100%', aspectRatio: 1, borderRadius: 16, background: CATEGORY_COLORS[i % CATEGORY_COLORS.length], display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32, transition: 'transform 0.2s', overflow: 'hidden' }}
                  onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.05)'}
                  onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                >
                  {cat.image ? <img src={cat.image} alt={cat.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (CATEGORY_EMOJI[cat.slug] || '🛍️')}
                </div>
                <span style={{ fontSize: 12, fontWeight: 500, color: '#374151', textAlign: 'center' }}>{cat.name}</span>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Flash Sale Banner */}
      <div style={{ background: 'linear-gradient(to right, #1a1a2e, #2d1b6e)', padding: '2.5rem 0', margin: '0' }}>
        <div className="container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 20 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
              <span style={{ background: '#f43397', color: 'white', padding: '4px 12px', borderRadius: 20, fontSize: 13, fontWeight: 700 }}>⚡ FLASH SALE</span>
              <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: 14 }}>Limited time offer</span>
            </div>
            <h2 style={{ color: 'white', fontSize: 32, fontWeight: 800 }}>Up to <span style={{ color: '#f43397' }}>70% OFF</span> Today!</h2>
            <p style={{ color: 'rgba(255,255,255,0.7)', marginTop: 6 }}>Use code <strong style={{ color: '#fbbf24' }}>WELCOME50</strong> for extra 50% off</p>
          </div>
          <Link to="/products" className="btn btn-primary btn-lg">
            Shop Now <ArrowRight size={18} />
          </Link>
        </div>
      </div>

      {/* Featured Products */}
      <div style={{ padding: '3rem 0' }}>
        <div className="container">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
            <h2 style={{ fontSize: 24, fontWeight: 700 }}>✨ Featured Products</h2>
            <Link to="/products?featured=true" style={{ color: '#f43397', fontSize: 14, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4 }}>
              View all <ArrowRight size={16} />
            </Link>
          </div>
          {isLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
              <div className="spinner" />
            </div>
          ) : (
            <div className="product-grid">
              {featuredData?.products?.map(p => <ProductCard key={p.id} product={p} />)}
            </div>
          )}
        </div>
      </div>

      {/* New Arrivals */}
      {newData?.products?.length > 0 && (
        <div style={{ padding: '0 0 3rem' }}>
          <div className="container">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
              <h2 style={{ fontSize: 24, fontWeight: 700 }}>🆕 New Arrivals</h2>
              <Link to="/products?sort=newest" style={{ color: '#f43397', fontSize: 14, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4 }}>
                View all <ArrowRight size={16} />
              </Link>
            </div>
            <div className="product-grid">
              {newData.products.slice(0, 8).map(p => <ProductCard key={p.id} product={p} />)}
            </div>
          </div>
        </div>
      )}

      {/* Stats */}
      <div style={{ background: '#f43397', padding: '3rem 0' }}>
        <div className="container">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 24, textAlign: 'center' }}>
            {[
              { num: '10M+', label: 'Happy Customers' },
              { num: '50L+', label: 'Products' },
              { num: '1.5L+', label: 'Sellers' },
              { num: '19K+', label: 'Cities Served' },
            ].map(stat => (
              <div key={stat.label}>
                <p style={{ fontSize: 36, fontWeight: 800, color: 'white' }}>{stat.num}</p>
                <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: 14 }}>{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
